import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import theme from "../../theme/theme";
import CustomTextBox from "../CustomTextBox/CustomTextBox";
import CustomButton from "../CustomButton/CustomButton";
import { emailRegex, passwordRegex } from "../../Regex";
import CustomModal from "../CustomModal/CustomModal";
import { Auth } from "aws-amplify";
import CustomAlertLabel from "../CustomAlertLabel/CustomAlertLabel";
import { setId, resetUserAccountDetails } from "../../redux/actions";
import LoadingBar from "../LoadingBar/LoadingBar";

const BodyWrapper = styled.div`
  text-align: center;
  min-width: 90%;

  .inputBoxSection {
    margin-top: 5%;
    width: 300px;

    @media (max-width: 480px) {
      width: 100%;
    }
  }
`;

const Header = styled.h2`
  font-size: 30px;
  font-weight: 700;
  color: ${theme.colors.primaryStrong2X};
`;

const LoginLink = styled.div`
  font-size: 15px;

  .loginButton {
    text-decoration: underline;
    cursor: pointer;
    color: ${theme.colors.primaryStrong2X};
  }
`;

const CreateAccountForm = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [userName, setUserName] = useState("");
  const [mailId, setMailId] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(isLoading);
  const [customModalProps, setCustomModalProps] = useState({
    isVisible: false,
    headerMessage: "",
    subHeaderMessage: "",
    primaryButtonText: "",
  });
  const [customAlertMessage, setCustomAlertMessage] = useState("");

  useEffect(() => {
    dispatch(resetUserAccountDetails());
  }, []);

  const getRefferalAdvisorId = () => {
    let advisorId = "";
    let url = window.location.href;
    if (url.toString().indexOf("advisorCode") > -1) {
      advisorId = url
        .toString()
        .substring(url.toString().indexOf("advisorCode"), url.toString().length)
        .split("=")[1];
    }
    return advisorId;
  };

  const handleCustomModalOnClose = () => {
    setCustomModalProps((state) => ({
      ...state,
      isVisible: false,
    }));
  };

  const signup = async (userName, password, mailId) => {
    setIsLoading(true);
    try {
      const signupSuccessResponse = await Auth.signUp({
        username: userName,
        password: password,
        attributes: {
          email: mailId,
          "custom:advisorId": getRefferalAdvisorId(),
        },
      });

      if (signupSuccessResponse) {
        dispatch(setId(mailId, password));
        history.push(`/mail-verification/${mailId}`);
      }
    } catch (error) {
      setCustomModalProps((state) => ({
        ...state,
        isVisible: true,
        headerMessage: error.message || "Something went wrong please try again",
        subHeaderMessage:
          error.name === "UsernameExistsException" ? "Try login instead" : "",
        primaryButtonText:
          error.name === "UsernameExistsException" ? "Login" : "Ok",
      }));
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccountClick = () => {
    if (mailId && userName && password && confirmPassword) {
      if (password !== confirmPassword) {
        setCustomAlertMessage("Confirm password is not matching");
        return;
      }
      setCustomAlertMessage("");
      signup(userName, password, mailId);
    } else {
      setCustomAlertMessage("All the fileds are mandatory");
    }
  };

  const handleCustomTextBoxOnBlur = (textBoxName, value) => {
    if (textBoxName === "mailId") {
      setMailId(value);
      setUserName(value);
    } else if (textBoxName === "password") {
      setPassword(value);
    } else if (textBoxName === "confirmPassword") {
      setConfirmPassword(value);
    }
  };

  const handleCustomModalPrimaryButtonClick = () => {
    if (customModalProps.primaryButtonText === "Login") history.push(`/login`);
    else
      setCustomModalProps((state) => ({
        ...state,
        isVisible: false,
      }));
  };

  const handleLoginLinkClick = () => {
    history.push("/login");
  };

  const handleCustomTextBoxKeyPress = (event) => {
    if (event.key === "Enter") {
      handleCreateAccountClick();
    }
  };

  return (
    <BodyWrapper>
      {(isLoaded || isLoading) && (
        <LoadingBar isLoading={isLoading} setIsLoaded={setIsLoaded} />
      )}
      {customModalProps.isVisible && (
        <CustomModal
          onClose={handleCustomModalOnClose}
          headerMessage={customModalProps.headerMessage}
          subHeaderMessage={customModalProps.subHeaderMessage}
          primaryButtonText={customModalProps.primaryButtonText}
          onClick={handleCustomModalPrimaryButtonClick}
        />
      )}
      <Header>Get Started</Header>
      <div className="inputBoxSection">
        <CustomTextBox
          placeholder="Email"
          outlined={true}
          errorMessage="Not a valid mail id"
          name="mailId"
          regexCode={emailRegex}
          onBlur={handleCustomTextBoxOnBlur}
          onKeyPress={handleCustomTextBoxKeyPress}
        />
      </div>
      <div className="inputBoxSection">
        <CustomTextBox
          placeholder="Password"
          outlined={true}
          errorMessage="Password should contain at least 8 characters, 1 uppercase, 1 lowercase character, 1 number and 1 special character (accepted special characters - !@#$%^&*())"
          name="password"
          type={"password"}
          regexCode={passwordRegex}
          onKeyPress={handleCustomTextBoxKeyPress}
          onBlur={handleCustomTextBoxOnBlur}
        />
      </div>
      <div className="inputBoxSection">
        <CustomTextBox
          placeholder="Confirm Password"
          outlined={true}
          errorMessage="Not a valid password"
          name="confirmPassword"
          type={"password"}
          regexCode={passwordRegex}
          onKeyPress={handleCustomTextBoxKeyPress}
          onBlur={handleCustomTextBoxOnBlur}
        />
      </div>
      <div className="inputBoxSection">
        <CustomButton
          text="Begin Ascent"
          onClick={handleCreateAccountClick}
          name="confirmPassword"
        />
        <CustomAlertLabel message={customAlertMessage} />
      </div>
      <div style={{ marginTop: "5px" }}>
        <LoginLink>
          Already an user?{" "}
          <span className="loginButton" onClick={handleLoginLinkClick}>
            Login
          </span>
        </LoginLink>
      </div>
    </BodyWrapper>
  );
};

export default CreateAccountForm;
